import { faAndroid, faApple } from '@fortawesome/free-brands-svg-icons';
import {
  faCube,
  faFileLines,
  faGears,
  faImages,
  faPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FluidButton, Stack, Step, Stepper } from '@sixui/ui';
import { Trans, useTranslation } from 'react-i18next';

import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';
import { useOnboardingStatusQuery } from '~/lib/api/modules/Onboarding/hooks/useOnboardingStatusQuery';

export type IOnboardingProps = {
  organizationId?: string;
  appId?: string;
};

export const Onboarding: React.FC<IOnboardingProps> = (props) => {
  const { organizationId: organizationIdProp, appId } = props;
  const { t } = useTranslation();
  const appQuery = useAppQuery(appId ? { appId } : undefined);
  const organizationId = organizationIdProp ?? appQuery.data?.organization?._id;
  const target = appId
    ? 'application'
    : organizationId
      ? 'organization'
      : 'user';
  const onboardingStatusQuery = useOnboardingStatusQuery(
    appQuery.isLoading
      ? undefined
      : {
          body: {
            target,
            id: appId || organizationId,
          },
        },
  );

  const getActiveStep = (): number | undefined => {
    if (!onboardingStatusQuery.data?.firstOrg) {
      return 0;
    }

    if (!organizationId) {
      return 1;
    }

    if (!onboardingStatusQuery.data?.firstApp) {
      return 2;
    }

    if (!appId) {
      return 3;
    }

    if (!onboardingStatusQuery.data?.firstCategory) {
      return 4;
    }

    if (!onboardingStatusQuery.data?.firstArticle) {
      return 5;
    }

    if (!onboardingStatusQuery.data?.assets) {
      return 6;
    }

    if (!onboardingStatusQuery.data?.appleTeamId) {
      return 7;
    }

    if (!onboardingStatusQuery.data?.firstBuild) {
      return 8;
    }

    return 9;
  };

  const activeStep = getActiveStep();
  const loading = appQuery.isLoading || onboardingStatusQuery.isLoading;

  return appQuery.isError ? (
    <ErrorMessage error={appQuery.error} />
  ) : onboardingStatusQuery.isError ? (
    <ErrorMessage error={onboardingStatusQuery.error} />
  ) : loading ? (
    t('common.loading')
  ) : (
    <Stepper orientation="vertical" activeStep={activeStep}>
      <Step label={t('Onboarding.steps.createOrganization.label')}>
        <Stack gap={2}>
          <Trans
            parent="div"
            i18nKey="Onboarding.steps.createOrganization.p1"
          />
          <Trans
            parent="div"
            i18nKey="Onboarding.steps.createOrganization.p2"
          />
          <Trans
            parent="div"
            i18nKey="Onboarding.steps.createOrganization.p3"
            components={{
              'create-organization': (
                <FluidButton href="/organizations/create">
                  <FontAwesomeIcon icon={faPlus} />{' '}
                  {t('OrganizationSelector.create')}
                </FluidButton>
              ),
            }}
          />
        </Stack>
      </Step>

      <Step label={t('Onboarding.steps.selectOrganization.label')}>
        <Stack gap={2}>
          <Trans
            parent="div"
            i18nKey="Onboarding.steps.selectOrganization.p1"
          />
          <Trans
            parent="div"
            i18nKey="Onboarding.steps.selectOrganization.p2"
          />
        </Stack>
      </Step>

      <Step label={t('Onboarding.steps.createApplication.label')}>
        <Stack gap={2}>
          <Trans
            parent="div"
            i18nKey="Onboarding.steps.createApplication.p1"
            components={{
              applications: (
                <FluidButton href="/apps">
                  <FontAwesomeIcon icon={faCube} />{' '}
                  {t('AppSidebarContent.applications')}
                </FluidButton>
              ),
              'create-application': (
                <FluidButton
                  href={`/organizations/${organizationId}/apps/create`}
                >
                  <FontAwesomeIcon icon={faPlus} /> {t('AppsPage.create')}
                </FluidButton>
              ),
            }}
          />
        </Stack>
      </Step>

      <Step label={t('Onboarding.steps.selectApplication.label')}>
        <Stack gap={2}>
          <Trans parent="div" i18nKey="Onboarding.steps.selectApplication.p1" />
          <Trans parent="div" i18nKey="Onboarding.steps.selectApplication.p2" />
        </Stack>
      </Step>

      <Step label={t('Onboarding.steps.createCategory.label')}>
        <Stack gap={2}>
          <Trans parent="div" i18nKey="Onboarding.steps.createCategory.p1" />
          <Trans
            parent="div"
            i18nKey="Onboarding.steps.createCategory.p2"
            components={{
              content: (
                <FluidButton href={`/apps/${appId}/content`}>
                  <FontAwesomeIcon icon={faFileLines} />{' '}
                  {t('AppSidebarContent.content')}
                </FluidButton>
              ),
              'create-category': (
                <FluidButton href={`/apps/${appId}/categories/create`}>
                  <FontAwesomeIcon icon={faPlus} />{' '}
                  {t('CategoryList.create-category')}
                </FluidButton>
              ),
            }}
          />
        </Stack>
      </Step>

      <Step label={t('Onboarding.steps.createArticle.label')}>
        <Stack gap={2}>
          <Trans parent="div" i18nKey="Onboarding.steps.createArticle.p1" />
          <Trans
            parent="div"
            i18nKey="Onboarding.steps.createArticle.p2"
            components={{
              content: (
                <FluidButton href={`/apps/${appId}/content`}>
                  <FontAwesomeIcon icon={faFileLines} />{' '}
                  {t('AppSidebarContent.content')}
                </FluidButton>
              ),
              'create-article': (
                <FluidButton href={`/apps/${appId}/articles/create`}>
                  <FontAwesomeIcon icon={faPlus} />{' '}
                  {t('ArticleList.create-article')}
                </FluidButton>
              ),
            }}
          />
        </Stack>
      </Step>

      <Step label={t('Onboarding.steps.assets.label')}>
        <Stack gap={2}>
          <Trans parent="div" i18nKey="Onboarding.steps.assets.p1" />
          <Trans
            parent="div"
            i18nKey="Onboarding.steps.assets.p2"
            components={{
              settings: (
                <FluidButton href={`/apps/${appId}/settings`}>
                  <FontAwesomeIcon icon={faGears} />{' '}
                  {t('AppSidebarContent.settings')}
                </FluidButton>
              ),
              assets: (
                <FluidButton href={`/apps/${appId}/settings/assets`}>
                  <FontAwesomeIcon icon={faImages} />{' '}
                  {t('AppSettings.cards.resources.title')}
                </FluidButton>
              ),
            }}
          />
        </Stack>
      </Step>

      <Step label={t('Onboarding.steps.appleTeamId.label')}>
        <Stack gap={2}>
          <Trans parent="div" i18nKey="Onboarding.steps.appleTeamId.p1" />
          <Trans
            parent="div"
            i18nKey="Onboarding.steps.appleTeamId.p2"
            components={{
              settings: (
                <FluidButton href={`/organizations/${organizationId}/settings`}>
                  <FontAwesomeIcon icon={faGears} />{' '}
                  {t('OrganizationSidebarContent.settings')}
                </FluidButton>
              ),
            }}
          />
        </Stack>
      </Step>

      <Step label={t('Onboarding.steps.firstBuild.label')}>
        <Stack gap={2}>
          <Trans parent="div" i18nKey="Onboarding.steps.firstBuild.p1" />
          <Trans
            parent="div"
            i18nKey="Onboarding.steps.firstBuild.p2"
            components={{
              'ios-build': (
                <FluidButton href={`/apps/${appId}/builds/ios`}>
                  <FontAwesomeIcon icon={faApple} />{' '}
                  {t('AppSidebarContent.builds')} /{' '}
                  {t('AppSidebarContent.ios-builds')}
                </FluidButton>
              ),
              'android-build': (
                <FluidButton href={`/apps/${appId}/builds/android`}>
                  <FontAwesomeIcon icon={faAndroid} />{' '}
                  {t('AppSidebarContent.builds')} /{' '}
                  {t('AppSidebarContent.android-builds')}
                </FluidButton>
              ),
            }}
          />
        </Stack>
      </Step>

      <Step label={t('Onboarding.steps.end.label')}>
        <Stack gap={2}>
          <Trans parent="div" i18nKey="Onboarding.steps.end.p1" />
          <Trans parent="div" i18nKey="Onboarding.steps.end.p2" />
        </Stack>
      </Step>
    </Stepper>
  );
};
